export type CrosswordDirection = 'across' | 'down';

export type ArrowDir = 'left' | 'down';

export type CrosswordPlacement = {
    id: string;
    text: string;
    answer: string;
    direction: CrosswordDirection;
    row: number;
    col: number;
};

export type TashbetzClue = CrosswordPlacement & {
    arrow: ArrowDir;
};

export type TashbetzClueCell = {
    kind: 'clue';
    clueId: string;
    arrow: ArrowDir;
};

export type TashbetzInputCell = {
    kind: 'input';
    letter: string;
};

export type TashbetzBlockCell = {
    kind: 'block';
};

export type TashbetzCell = TashbetzClueCell | TashbetzInputCell | TashbetzBlockCell;

export type CrosswordBoard = {
    rows: number;
    cols: number;
    cells: TashbetzCell[][];
    clues: TashbetzClue[];
};

export type CrosswordMeta = {
    dateKey: string;
    dateLabel: string;
    number: number;
};

export type CrosswordPayload = {
    id: string;
    meta: CrosswordMeta;
    board: CrosswordBoard;
};

export const DEFAULT_CROSSWORD_ROWS = 9;
export const DEFAULT_CROSSWORD_COLS = 16;

/** First published daily puzzle (local Israel date). */
export const PUZZLE_EPOCH = '2026-09-04';

export const CROSSWORD_SEED_COUNT = 30;

/**
 * Clue variants per answer, rotated across the daily puzzles.
 *
 * Rules enforced by `scripts/audit-crossword-clues.ts`:
 * a clue never contains its own answer or any other answer on the board,
 * every clue text is unique, and content words are not reused between clues.
 * Ashkelon references lean on history and landmarks, not neighborhood names.
 */
export const CROSSWORD_CLUE_VARIANTS: Record<string, string[]> = {
    city: [
        'מחמש ערי הפלשתים',
        'כאן שוכן מרכז ברזילי',
        'הוקמה מחדש בשנות החמישים',
    ],
    light: ['היפוכו של חושך', 'הנורה פולטת אותו', 'מהיר ממנו אין דבר'],
    sun: ['זורחת במזרח', 'הכוכב הקרוב אלינו ביותר', 'סביבה מקיפים כוכבי הלכת'],
    straw: ['שותים דרכו', 'נותר בשדה לאחר הקציר', 'גג הבקתה עשוי ממנו'],
    night: ['זמן השינה', 'כשהכוכבים יוצאים', 'בין ערב לבוקר'],
    rose: ['פרח אדום עם קוצים', 'מתנה קלאסית לאהובה', 'מפיקים ממנו בושם יקר'],
    sea: ['התיכון, סוף או המלח', 'גדול מאגם וקטן מאוקיינוס', 'כינרת אינה כזה'],
    port: ['אשדוד וחיפה מפורסמות בו', 'מכולות נפרקות בו', 'שער הסחורות של המדינה'],
    wave: ['ערמת אבנים', 'תדר שבו משדרים', 'נשבר אל הסלע'],
    fish: ['סלמון או טונה', 'נושם דרך זימים', 'שוחה בעזרת סנפיר'],
    beach: ['שוכבים עליו עם מגבת', 'המציל שומר עליו', 'קו המפגש של היבשה'],
    garden: ['ילדים בני שלוש לומדים בו', 'עדן המקראי', 'חיות בר מוצגות בו'],
    tower: ['בבל בנתה אחד', 'פיזה נוטה', 'בניין גבוה וצר'],
    sand: ['בונים ממנו ארמונות', 'ממלא את שעון הזכוכית', 'הסהרה מלאה בו'],
    heat: ['התנור בתום האפייה', 'ניגודו של קר', 'אוגוסט באילת'],
    marina: ['בית לכלי שיט פרטיים', 'יאכטות חונות בו', 'רציף לשייט קטן'],
    water: ['שני מימנים וחמצן', 'הברז מספק אותם', 'בלעדיהם לא מתקיימים חיים'],
    boat: ['משוטים מניעים אותה', 'קטנה מאונייה', 'מפליגים בה למרחק קצר'],
    only: ['בלבד, ותו לא', 'מילת הגבלה', 'ולא יותר מכך'],
    sunset: ['סיום היום במערב', 'השעה היפה לצילום', 'כשהאופק נצבע בכתום'],
    mountain: ['החרמון או התבור', 'אוורסט הגבוה בעולם', 'תלול ומתנשא'],
    fisherman: ['עבודתו ברשת ובחכה', 'פורש רשתות עם שחר', 'פרנסתו מן המצולות'],
    afridar: [
        'השכונה שהקימו יוצאי דרום אפריקה',
        'בלבה כיכר מסחרית עגולה',
        'נבנתה מתרומות יהדות התפוצות',
    ],
    barnea: ['קדש ____ במדבר', 'רובע מגורים בצפון', 'אזור הווילות הצפוני'],
    pool: ['שוחים בה בקיץ', 'יש בה מסלולים ומקפצה', 'בכל מלון מצויה אחת'],
    promenade: ['רחוב להולכי רגל מול הנוף', 'תל אביב מפורסמת באחת', 'מסלול מרוצף עם ספסלים'],
};

/**
 * Swedish-style Ashkelon board.
 * Column 0 is the visual left. Across clues sit to the right of the word and
 * point left. Down clues sit above the word and point down.
 */
export const DEFAULT_CROSSWORD_PLACEMENTS: CrosswordPlacement[] = [
    { id: 'city', text: 'מחמש ערי הפלשתים', answer: 'אשקלון', direction: 'across', row: 2, col: 10 },
    { id: 'light', text: 'היפוכו של חושך', answer: 'אור', direction: 'down', row: 1, col: 9 },
    { id: 'sun', text: 'זורחת במזרח', answer: 'שמש', direction: 'down', row: 1, col: 8 },
    { id: 'straw', text: 'שותים דרכו', answer: 'קש', direction: 'down', row: 1, col: 7 },
    { id: 'night', text: 'זמן השינה', answer: 'לילה', direction: 'down', row: 1, col: 6 },
    { id: 'rose', text: 'פרח אדום עם קוצים', answer: 'ורד', direction: 'down', row: 1, col: 5 },
    { id: 'sea', text: 'התיכון, סוף או המלח', answer: 'ים', direction: 'down', row: 0, col: 0 },
    { id: 'port', text: 'אשדוד וחיפה מפורסמות בו', answer: 'נמל', direction: 'down', row: 0, col: 1 },
    { id: 'wave', text: 'ערמת אבנים', answer: 'גל', direction: 'across', row: 0, col: 4 },
    { id: 'fish', text: 'סלמון או טונה', answer: 'דג', direction: 'across', row: 0, col: 7 },
    { id: 'beach', text: 'שוכבים עליו עם מגבת', answer: 'חוף', direction: 'down', row: 0, col: 12 },
    { id: 'garden', text: 'ילדים בני שלוש לומדים בו', answer: 'גן', direction: 'down', row: 0, col: 14 },
    { id: 'tower', text: 'בבל בנתה אחד', answer: 'מגדל', direction: 'down', row: 0, col: 15 },
    { id: 'sand', text: 'בונים ממנו ארמונות', answer: 'חול', direction: 'across', row: 4, col: 3 },
    { id: 'heat', text: 'התנור בתום האפייה', answer: 'חם', direction: 'down', row: 3, col: 2 },
    { id: 'marina', text: 'בית לכלי שיט פרטיים', answer: 'מעגן', direction: 'across', row: 4, col: 14 },
    { id: 'water', text: 'שני מימנים וחמצן', answer: 'מים', direction: 'down', row: 3, col: 13 },
    { id: 'boat', text: 'משוטים מניעים אותה', answer: 'סירה', direction: 'across', row: 5, col: 10 },
    { id: 'only', text: 'בלבד, ותו לא', answer: 'רק', direction: 'down', row: 4, col: 7 },
    { id: 'sunset', text: 'סיום היום במערב', answer: 'שקיעה', direction: 'across', row: 6, col: 9 },
    { id: 'mountain', text: 'החרמון או התבור', answer: 'הר', direction: 'down', row: 5, col: 4 },
    { id: 'fisherman', text: 'עבודתו ברשת ובחכה', answer: 'דייג', direction: 'across', row: 5, col: 15 },
    {
        id: 'afridar',
        text: 'השכונה שהקימו יוצאי דרום אפריקה',
        answer: 'אפרידר',
        direction: 'across',
        row: 7,
        col: 7,
    },
    { id: 'barnea', text: 'קדש ____ במדבר', answer: 'ברנע', direction: 'across', row: 7, col: 13 },
    { id: 'pool', text: 'שוחים בה בקיץ', answer: 'בריכה', direction: 'across', row: 8, col: 6 },
    {
        id: 'promenade',
        text: 'רחוב להולכי רגל מול הנוף',
        answer: 'טיילת',
        direction: 'across',
        row: 8,
        col: 15,
    },
];

function arrowFor(direction: CrosswordDirection): ArrowDir {
    return direction === 'across' ? 'left' : 'down';
}

function cluePath(placement: CrosswordPlacement): Array<{ row: number; col: number }> {
    const cells: Array<{ row: number; col: number }> = [];
    for (let i = 0; i < placement.answer.length; i += 1) {
        cells.push({
            row: placement.row + (placement.direction === 'down' ? i + 1 : 0),
            col: placement.col + (placement.direction === 'across' ? -(i + 1) : 0),
        });
    }
    return cells;
}

export function buildBoard(
    placements: CrosswordPlacement[],
    rows = DEFAULT_CROSSWORD_ROWS,
    cols = DEFAULT_CROSSWORD_COLS,
): CrosswordBoard {
    const cells: TashbetzCell[][] = Array.from({ length: rows }, () =>
        Array.from({ length: cols }, () => ({ kind: 'block' as const })),
    );

    for (const placement of placements) {
        const clueCell = cells[placement.row]?.[placement.col];
        if (!clueCell || clueCell.kind !== 'block') {
            throw new Error(`Clue cell busy: ${placement.id} at ${placement.row},${placement.col}`);
        }
        cells[placement.row]![placement.col] = {
            kind: 'clue',
            clueId: placement.id,
            arrow: arrowFor(placement.direction),
        };

        const path = cluePath(placement);
        for (let i = 0; i < path.length; i += 1) {
            const { row, col } = path[i]!;
            if (row < 0 || col < 0 || row >= rows || col >= cols) {
                throw new Error(`Out of bounds: ${placement.id} ${row},${col}`);
            }
            const existing = cells[row]![col]!;
            const letter = placement.answer[i]!;
            if (existing.kind === 'block') {
                cells[row]![col] = { kind: 'input', letter };
            } else if (existing.kind === 'input' && existing.letter === letter) {
                continue;
            } else {
                const got = existing.kind === 'input' ? existing.letter : existing.clueId;
                throw new Error(
                    `Conflict: ${placement.id} at ${row},${col} wants ${letter}, has ${got}`,
                );
            }
        }
    }

    return {
        rows,
        cols,
        cells,
        clues: placements.map((placement) => ({
            ...placement,
            arrow: arrowFor(placement.direction),
        })),
    };
}

const FINAL_FORMS: Record<string, string> = {
    כ: 'ך',
    מ: 'ם',
    נ: 'ן',
    פ: 'ף',
    צ: 'ץ',
};

const REGULAR_FORMS: Record<string, string> = Object.fromEntries(
    Object.entries(FINAL_FORMS).map(([regular, final]) => [final, regular]),
);

export function normalizeHebrewLetter(value: string): string {
    const ch = value.trim().slice(-1);
    if (!ch) return '';
    return REGULAR_FORMS[ch] ?? ch;
}

export function lettersMatch(user: string, solution: string): boolean {
    if (!user) return false;
    const normalizedUser = normalizeHebrewLetter(user);
    const normalizedSolution = normalizeHebrewLetter(solution);
    if (normalizedUser === normalizedSolution) return true;
    const userFinal = FINAL_FORMS[normalizedUser] ?? normalizedUser;
    const solutionFinal = FINAL_FORMS[normalizedSolution] ?? normalizedSolution;
    return userFinal === solutionFinal;
}

export function cellKey(row: number, col: number): string {
    return `${row},${col}`;
}

export function getClueById(board: CrosswordBoard, clueId: string): TashbetzClue | undefined {
    return board.clues.find((clue) => clue.id === clueId);
}

export function getClueCells(clue: TashbetzClue): Array<{ row: number; col: number }> {
    return cluePath(clue);
}

export function getCluesForCell(board: CrosswordBoard, row: number, col: number): TashbetzClue[] {
    return board.clues.filter((clue) =>
        getClueCells(clue).some((cell) => cell.row === row && cell.col === col),
    );
}

export function getInputCells(
    board: CrosswordBoard,
): Array<{ row: number; col: number; letter: string }> {
    const inputs: Array<{ row: number; col: number; letter: string }> = [];
    for (let row = 0; row < board.rows; row += 1) {
        for (let col = 0; col < board.cols; col += 1) {
            const cell = board.cells[row]![col];
            if (cell.kind === 'input') {
                inputs.push({ row, col, letter: cell.letter });
            }
        }
    }
    return inputs;
}

export function getFirstInputForClue(clue: TashbetzClue): { row: number; col: number } {
    const [first] = getClueCells(clue);
    return first ?? { row: clue.row, col: clue.col };
}

export function nextInputCell(
    row: number,
    col: number,
    clue: TashbetzClue,
    reverse = false,
): { row: number; col: number } | null {
    const cells = getClueCells(clue);
    const index = cells.findIndex((cell) => cell.row === row && cell.col === col);
    if (index === -1) return null;
    const nextIndex = reverse ? index - 1 : index + 1;
    return cells[nextIndex] ?? null;
}

export function getSolutionLetter(board: CrosswordBoard, row: number, col: number): string | null {
    const cell = board.cells[row]?.[col];
    if (cell?.kind !== 'input') return null;
    return cell.letter;
}

const JERUSALEM = 'Asia/Jerusalem';

export function jerusalemDateKey(now = new Date()): string {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: JERUSALEM,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(now);
}

export function daysBetweenKeys(from: string, to: string): number {
    const fromUtc = Date.parse(`${from}T00:00:00Z`);
    const toUtc = Date.parse(`${to}T00:00:00Z`);
    return Math.floor((toUtc - fromUtc) / 86_400_000);
}

export function addDaysToDateKey(dateKey: string, delta: number): string {
    const ms = Date.parse(`${dateKey}T00:00:00Z`) + delta * 86_400_000;
    return new Date(ms).toISOString().slice(0, 10);
}

export function formatCrosswordDateLabel(dateKey: string): string {
    return new Intl.DateTimeFormat('he-IL', {
        timeZone: JERUSALEM,
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        numberingSystem: 'latn',
    }).format(new Date(`${dateKey}T12:00:00+03:00`));
}

export function getDailyCrosswordMeta(now = new Date()): CrosswordMeta {
    const dateKey = jerusalemDateKey(now);
    return {
        dateKey,
        dateLabel: formatCrosswordDateLabel(dateKey),
        number: Math.max(1, daysBetweenKeys(PUZZLE_EPOCH, dateKey) + 1),
    };
}

export function validateCrosswordPlacements(
    placements: CrosswordPlacement[],
    rows = DEFAULT_CROSSWORD_ROWS,
    cols = DEFAULT_CROSSWORD_COLS,
): CrosswordBoard {
    return buildBoard(placements, rows, cols);
}
